import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

interface TokenDB extends DBSchema {
  tokens: {
    key: string;
    value: {
      token: string;
      expiresAt?: number;
    };
  };
}

class SecureTokenService {
  private dbName = 'secure-auth-db';
  private dbVersion = 1;
  private db: IDBPDatabase<TokenDB> | null = null;
  private dbAvailable = false;

  // In-memory storage for access token with sessionStorage backup
  private accessToken: string | null = null;

  // Keys for sessionStorage (obfuscated names)
  private readonly ACCESS_TOKEN_KEY = 'sat'; // Short key to obscure purpose
  private readonly REFRESH_TOKEN_KEY = 'srt'; // Short key to obscure purpose

  constructor() {
    // Initialize access token from sessionStorage on load
    this.initializeAccessToken();
    // Check IndexedDB availability early
    this.checkIndexedDBAvailability();
  }

  /**
   * Check if IndexedDB is available in this environment
   * Handles: Private mode, WebView, old browsers, quota exceeded
   */
  private checkIndexedDBAvailability(): void {
    try {
      if (
        typeof window === 'undefined' ||
        typeof window.indexedDB === 'undefined'
      ) {
        this.dbAvailable = false;
        console.debug(
          'IndexedDB not available (Private mode or restricted environment)'
        );
        return;
      }
      this.dbAvailable = true;
    } catch (error) {
      this.dbAvailable = false;
      console.debug('Failed to check IndexedDB availability:', error);
    }
  }

  private initializeAccessToken(): void {
    // Try to recover access token from sessionStorage
    const storedToken = sessionStorage.getItem(this.ACCESS_TOKEN_KEY);
    if (storedToken) {
      try {
        // Simple obfuscation - not encryption but better than plain text
        const decoded = atob(storedToken);
        this.accessToken = decoded;
      } catch {
        // Invalid token in storage, clear it
        sessionStorage.removeItem(this.ACCESS_TOKEN_KEY);
      }
    }
  }

  /**
   * Initialize IndexedDB connection
   * Safely handles cases where IndexedDB is unavailable
   */
  async initDB(): Promise<void> {
    if (this.db) return;

    // If IndexedDB is not available, skip initialization
    if (!this.dbAvailable) {
      return;
    }

    try {
      this.db = await openDB<TokenDB>(this.dbName, this.dbVersion, {
        upgrade(db) {
          if (!db.objectStoreNames.contains('tokens')) {
            db.createObjectStore('tokens');
          }
        },
      });
    } catch (error) {
      // IndexedDB initialization failed - fall back to sessionStorage
      console.warn(
        'IndexedDB initialization failed, falling back to sessionStorage:',
        error
      );
      this.dbAvailable = false;
      this.db = null;
    }
  }

  // Access token management (memory + sessionStorage for refresh persistence)
  setAccessToken(token: string): void {
    this.accessToken = token;
    // Store in sessionStorage with simple obfuscation
    // This persists during page refresh but clears when tab closes
    try {
      const encoded = btoa(token);
      sessionStorage.setItem(this.ACCESS_TOKEN_KEY, encoded);
    } catch {
      // If encoding fails, don't store (better to re-login than risk exposure)
    }
  }

  getAccessToken(): string | null {
    // First check memory
    if (this.accessToken) return this.accessToken;

    // Try to recover from sessionStorage (in case of page refresh)
    this.initializeAccessToken();
    return this.accessToken;
  }

  clearAccessToken(): void {
    this.accessToken = null;
    sessionStorage.removeItem(this.ACCESS_TOKEN_KEY);
  }

  /**
   * Refresh token management (IndexedDB for PWA offline support, SessionStorage fallback)
   * Gracefully degrades when IndexedDB is unavailable (Private mode, WebView, etc.)
   */
  async setRefreshToken(
    token: string,
    expiresInSeconds?: number
  ): Promise<void> {
    const expiresAt = expiresInSeconds
      ? Date.now() + expiresInSeconds * 1000
      : undefined;

    // Always store in SessionStorage first as primary fallback
    // This ensures token survives page refresh even without IndexedDB
    try {
      const encoded = btoa(token);
      sessionStorage.setItem(this.REFRESH_TOKEN_KEY, encoded);
    } catch (error) {
      console.warn('Failed to store token in SessionStorage:', error);
      // Continue anyway - try to use IndexedDB
    }

    // Also try to store in IndexedDB for better persistence (PWA offline support)
    if (this.dbAvailable) {
      try {
        await this.initDB();
        if (this.db) {
          await this.db.put(
            'tokens',
            {
              token,
              expiresAt,
            },
            'refresh'
          );
        }
      } catch (error) {
        // IndexedDB write failed, but token is safely in SessionStorage
        console.warn(
          'Failed to store token in IndexedDB, using SessionStorage:',
          error
        );
      }
    }
  }

  /**
   * Get refresh token from storage
   * Tries SessionStorage first (fastest), falls back to IndexedDB if available
   * Handles expired tokens and missing IndexedDB gracefully
   */
  async getRefreshToken(): Promise<string | null> {
    // First try session storage for performance (no async needed)
    const sessionToken = sessionStorage.getItem(this.REFRESH_TOKEN_KEY);
    if (sessionToken) {
      try {
        return atob(sessionToken);
      } catch {
        sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
      }
    }

    // Only try IndexedDB if available (not in Private mode)
    if (this.dbAvailable) {
      try {
        await this.initDB();
        if (this.db) {
          const data = await this.db.get('tokens', 'refresh');

          if (!data) return null;

          // Check if token is expired
          if (data.expiresAt && data.expiresAt < Date.now()) {
            await this.clearRefreshToken();
            return null;
          }

          return data.token;
        }
      } catch (error) {
        console.warn('Failed to get token from IndexedDB:', error);
        // Fall through and return null
      }
    }

    // No token available in any storage
    return null;
  }

  async clearRefreshToken(): Promise<void> {
    sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);

    // Try to clear from IndexedDB if available
    if (this.dbAvailable) {
      try {
        await this.initDB();
        if (this.db) {
          await this.db.delete('tokens', 'refresh');
        }
      } catch (error) {
        console.warn('Failed to clear token from IndexedDB:', error);
        // SessionStorage is already cleared, so this is not critical
      }
    }
  }

  // Clear all tokens
  async clearAll(): Promise<void> {
    this.clearAccessToken();
    await this.clearRefreshToken();
    // Clear all session storage tokens
    sessionStorage.removeItem(this.ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }

  // Check if we have valid tokens
  hasAccessToken(): boolean {
    return this.accessToken !== null;
  }

  async hasRefreshToken(): Promise<boolean> {
    const token = await this.getRefreshToken();
    return token !== null;
  }

  /**
   * Migration helper to move from localStorage
   * Handles gracefully if IndexedDB is unavailable
   */
  async migrateFromLocalStorage(): Promise<void> {
    const oldAccessToken = localStorage.getItem('access_token');
    const oldRefreshToken = localStorage.getItem('refresh_token');

    if (oldAccessToken) {
      this.setAccessToken(oldAccessToken);
      localStorage.removeItem('access_token');
    }

    if (oldRefreshToken) {
      try {
        await this.setRefreshToken(oldRefreshToken);
        localStorage.removeItem('refresh_token');
      } catch (error) {
        console.warn(
          'Failed to migrate refresh token from localStorage:',
          error
        );
        // Token migration failed, but it's not critical - user will just need to re-login
      }
    }
  }

  /**
   * Get information about current storage mode (for debugging/monitoring)
   */
  getStorageInfo(): { idbAvailable: boolean; idbConnected: boolean } {
    return {
      idbAvailable: this.dbAvailable,
      idbConnected: this.db !== null,
    };
  }
}

const secureTokenService = new SecureTokenService();

// Export singleton instance
export { secureTokenService };
export default secureTokenService;
